import React, { useState } from 'react';
import { Modal, Typography, Spin, Form, Input, Button, Row } from 'antd';
import { VerificationStatus } from '../../../utils/hooks/useWorldCheckVerification.hook';
import { ReactComponent as CheckCircleSuccess } from '../../../common/images/svg/CheckCircleSuccess.svg';
import { ReactComponent as CheckCircleWarning } from '../../../common/images/svg/CheckCircleWarning.svg';
import { ReactComponent as CheckCircleError } from '../../../common/images/svg/CheckCircleError.svg';
import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { companyNameRules, companyRegistrationNumberRules } from '../../../pages/payment-request/addCompany.constants';
import { AutoComplete } from '../../../components/inputs/autocomplete';
import { ROOT_URL, httpClient } from '../../../App';
import { onlyLatinCharsDigitsSpecialSymbolsRule } from '../../../utils/form.constants';
import { fetchFurtherInformation } from '../../../utils/fetchFurtherInformation';
import worldCheckLogo from '../../../common/images/png/world-check.png';

interface ComponentProps {
  onClose: () => void;
  status?: VerificationStatus;
  updateVerificationStatus: () => Promise<void>;
}

export function WorldcheckVerificationModal({ onClose, status, updateVerificationStatus }: ComponentProps) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchingDetails, setFetchingDetails] = useState<boolean>(false);

  function getTitle() {
    let text;
    switch (status) {
      case VerificationStatus.REQUIRED:
        text = 'We need to check your company against sanctions lists before you can start using the SMART platform';
        break;
      case VerificationStatus.PENDING:
        text = 'Verification in progress';
        break;
      case VerificationStatus.LOW_RISK:
        text = 'Low risk';
        break;
      case VerificationStatus.MEDIUM_RISK:
        text = 'Medium risk';
        break;
      case VerificationStatus.HIGH_RISK_FIR:
        text = (
          <span>
            High risk <br />
            Further Investigation Required
          </span>
        );
        break;
      case VerificationStatus.HIGH_RISK_EDD:
        text = (
          <span>
            High risk <br /> Enhanced Due Diligence
          </span>
        );
        break;
    }

    return <h5 style={{ textAlign: 'center' }}>{text}</h5>;
  }

  async function handleSubmit() {
    try {
      setLoading(true);
      await form.validateFields();
      const payload = form.getFieldsValue();
      await httpClient.post(`${ROOT_URL}/api/v1/worldCheck`, payload);
      await updateVerificationStatus();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function handleFetchFurtherInformation() {
    await fetchFurtherInformation(undefined, undefined, setFetchingDetails);
  }

  function getInnerContent() {
    if (fetchingDetails) {
      return (
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 100, color: 'blue', marginTop: 50, marginBottom: 50 }} spin />}
        />
      );
    }

    switch (status) {
      case VerificationStatus.REQUIRED:
        return (
          <>
            <Typography style={{ opacity: 0.7 }}>
              Please provide your company details below and submit the form.
              <br />
              <span style={{ fontWeight: 800 }}>Note: </span>
              <span>all the fields are required.</span>
            </Typography>
            <img src={worldCheckLogo} width='100%' />
            <Form layout='vertical' form={form} style={{ marginTop: 50, marginBottom: 20, width: '100%' }}>
              <Form.Item name='companyName' rules={companyNameRules}>
                <Input placeholder='Full name/Company name' size='large' />
              </Form.Item>
              <Form.Item name='regNo' rules={companyRegistrationNumberRules}>
                <Input placeholder='Reg. No (TIN, VAT)' size='large' />
              </Form.Item>
              <Form.Item
                name='country'
                rules={[
                  {
                    required: true,
                    message: 'Country of incorporation is required',
                    validateTrigger: 'onSubmit',
                  },
                ]}
              >
                <AutoComplete
                  resource='api/v1/worldCheck/countries'
                  mapItem={(item: { value: string; label: string }) => ({
                    value: item.label,
                    key: item.value,
                    label: <Typography.Text>{item.label}</Typography.Text>,
                    data: item,
                  })}
                  notFoundPlaceholder='Start by typing country name'
                >
                  <Input
                    // style={{ fontSize: 16 }}
                    placeholder='Country of incorporation'
                    size='large'
                    suffix={<DownOutlined />}
                  />
                </AutoComplete>
              </Form.Item>
              <Form.Item
                name='individualName'
                rules={[
                  onlyLatinCharsDigitsSpecialSymbolsRule,
                  {
                    required: true,
                    message: "Director's full name is required",
                    validateTrigger: 'onSubmit',
                    whitespace: true,
                  },
                ]}
              >
                <Input placeholder="Director's full name" size='large' />
              </Form.Item>
            </Form>
          </>
        );
      case VerificationStatus.PENDING:
        return (
          <>
            <Typography style={{ opacity: 0.7 }}>
              Your request is being processed. You can wait for it to complete, or
              <br /> you can close this window and refresh the home page later to see
              <br /> the updated status in the bottom left corner.
            </Typography>
            <Spin
              indicator={
                <LoadingOutlined style={{ fontSize: 100, color: 'blue', marginTop: 50, marginBottom: 50 }} spin />
              }
            />
          </>
        );
      case VerificationStatus.LOW_RISK:
        return (
          <>
            <Typography style={{ opacity: 0.7 }}>
              Your company has passed verification, all the features are
              <br /> unlocked.
            </Typography>
            <CheckCircleSuccess style={{ marginTop: 50, marginBottom: 50 }} />
          </>
        );
      case VerificationStatus.MEDIUM_RISK:
        return (
          <>
            <Typography style={{ opacity: 0.7 }}>
              Your company is verified, but might be blocked later. For
              <br /> more information, you can contact administrator at
              <br />
              <a style={{ opacity: 1 }} href='mailto:operations@altcapitalfund.com'>
                operations@altcapitalfund.com
              </a>
            </Typography>
            <CheckCircleWarning style={{ marginTop: 50, marginBottom: 50 }} />
          </>
        );
      case VerificationStatus.HIGH_RISK_FIR:
      case VerificationStatus.HIGH_RISK_EDD:
        return (
          <>
            {status === VerificationStatus.HIGH_RISK_FIR ? (
              <Typography style={{ fontSize: 16, marginBottom: 20, opacity: 0.7 }}>
                Your company is at high risk, no features are available
                <br /> at this moment.
              </Typography>
            ) : (
              <Typography style={{ fontSize: 16, marginBottom: 20, opacity: 0.7 }}>
                Your company is at high risk
              </Typography>
            )}
            <Typography style={{ opacity: 0.5 }}>Your company did not pass the sanctions list verification.</Typography>
            <Typography>
              <a onClick={handleFetchFurtherInformation}>See details.</a>
            </Typography>
            <Typography style={{ opacity: 0.5 }}>
              To resolve the issue, please contact administrator
              <br /> at <span style={{ textDecoration: 'underline' }}>operations@altcapitalfund.com</span>
            </Typography>
            <CheckCircleError style={{ marginTop: 50, marginBottom: 50 }} />
          </>
        );
    }
  }

  function getFooterButtons() {
    if (status === VerificationStatus.REQUIRED) {
      return (
        <Row style={{ width: '100%', justifyContent: 'center' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type='primary' onClick={handleSubmit} disabled={loading}>
            Submit
          </Button>
        </Row>
      );
    }

    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onClose}>OK, Got It</Button>
      </div>
    );
  }

  return (
    <Modal open onCancel={onClose} title={getTitle()} footer={getFooterButtons()}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        {getInnerContent()}
        {status !== VerificationStatus.REQUIRED && <img src={worldCheckLogo} width='100%' />}
      </div>
    </Modal>
  );
}
